<template>
  <div class="modal-window">
    <button type="button" @click="$emit('close')" class="button button--close">
      <IconClose class="icon icon--medium-large" />
    </button>
    <form>
      <div class="layout--text-left modal-window__wrap">
        <v-wait for="loading change summary">
          <spring-spinner
            :animation-duration="1500"
            :size="53"
            :color="'#b6bbff'"
            transition="fade"
            mode="out-in"
            :duration="1000"
            enter-active-class="enter-active"
            slot="waiting"
          />
          <div>
            <Legend class="modal-window__title">Close Account Options</Legend>
            <!-- only offer to downgrade if not on free account already -->
            <div v-if="subscription.subscriptionType !== 0">
              <input
                type="radio"
                id="downgrade-account"
                name="account-option"
                value="Downgrade"
                class="modal-window__option-radio"
                v-model="selectedOption"
              />
              <label for="downgrade-account">
                <div
                  class="modal-window__option"
                  v-bind:class="{
                    'modal-window__option--active':
                      selectedOption === 'Downgrade',
                  }"
                >
                  <div class="layout--flex-start">
                    <div class="layout--margin-right-xs">
                      <IconCheckboxChecked
                        v-if="selectedOption === 'Downgrade'"
                        class="icon icon--medium layout--margin-right-xs"
                      />
                      <IconCheckboxUnchecked
                        v-if="selectedOption !== 'Downgrade'"
                        class="icon icon--medium layout--margin-right-xs"
                      />
                    </div>
                    <div>
                      <p class="text-size-sm layout--margin-bottom-xxs">
                        <strong>Switch to Free Plan</strong>
                        <i> (Recommended)</i>
                      </p>
                      <p class="modal-window__option-copy">
                        Ends your paid subscription but still allows you to keep
                        your login to access existing timers and invoices.
                      </p>
                      <p v-show="transaction" class="layout--margin-top-sm">
                        <strong>${{ transaction }}</strong> ({{
                          transactionType | capitalizeFirstLetter
                        }}
                        today)
                      </p>
                    </div>
                  </div>
                </div>
              </label>
            </div>
            <input
              type="radio"
              id="close-account"
              name="account-option"
              value="Close Account"
              class="modal-window__option-radio"
              v-model="selectedOption"
            />
            <label for="close-account">
              <div
                class="modal-window__option"
                v-bind:class="{
                  'modal-window__option--active':
                    selectedOption === 'Close Account',
                }"
              >
                <div class="layout--flex-start">
                  <div class="layout--margin-right-xs">
                    <IconCheckboxChecked
                      v-if="selectedOption === 'Close Account'"
                      class="icon icon--medium layout--margin-right-xs"
                    />
                    <IconCheckboxUnchecked
                      v-if="selectedOption !== 'Close Account'"
                      class="icon icon--medium layout--margin-right-xs"
                    />
                  </div>
                  <div>
                    <p
                      class="text-size-sm weight--bold layout--margin-bottom-xxs"
                    >
                      Close Account
                    </p>
                    <p class="modal-window__option-copy">
                      Closing your account removes the ability to login into
                      MotionMail with this email address. Timers, invoices, etc.
                      will not be accessible.
                    </p>
                    <p v-show="transaction" class="layout--margin-top-sm">
                      <strong>${{ transaction }}</strong> ({{
                        transactionType | capitalizeFirstLetter
                      }}
                      today)
                    </p>
                  </div>
                </div>
              </div>
            </label>
          </div>
        </v-wait>
        <div
          class="layout--margin-top-md layout--margin-bottom-md layout--margin-left-sm layout--margin-right-sm"
        >
          <v-checkbox v-model="agreement">
            <template v-slot:label>
              <div>
                I agree to the
                <a
                  href="http://motionmailapp.com/terms"
                  class="link"
                  @click.stop
                  >Terms of Use</a
                >
                and
                <a
                  href="http://motionmailapp.com/privacy"
                  class="link"
                  @click.stop
                  >Privacy Policy</a
                >
              </div>
            </template>
          </v-checkbox>
          <div
            class="input-group__error"
            v-if="!$v.agreement.sameAs && $v.agreement.$dirty"
            role="alert"
          >
            You must agree in order to continue
          </div>
          <div class="layout--flex-space-between layout--margin-top-sm">
            <LoadingButton
              :event="handleSave"
              :loading="
                $wait.is('downgrading subscription') ||
                  $wait.is('closing account')
              "
              :label="buttonText"
            />
            <button
              type="button"
              class="button button--secondary"
              @click="$emit('close')"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { mapWaitingActions } from "vue-wait";
import { sameAs } from "vuelidate/lib/validators";

import { getBillingTransactionTypeDescription } from "../../../helpers/helpers";

import LoadingButton from "../../../components/LoadingButton";
import IconClose from "../../../assets/icons/iconCloseX.svg";
import IconCheckboxChecked from "../../../assets/icons/checkbox-Checked.svg";
import IconCheckboxUnchecked from "../../../assets/icons/checkbox-Unchecked.svg";

export default {
  name: "close-account-modal",
  components: {
    IconClose,
    IconCheckboxChecked,
    IconCheckboxUnchecked,
    LoadingButton,
  },
  data() {
    return {
      selectedOption: "Downgrade",
      agreement: false,
      updateError: false,
      changeSummary: null,
    };
  },
  mounted: function() {
    // if the user is on a free account, select close account by default
    if (this.subscription.subscriptionType === 0) {
      this.selectedOption = "Close Account";
    }
    // get change summary for downgrading account to free or canceling
    if (!this.changeSummary) {
      this.getChangeSummary({ subscriptionType: 0, billingCycle: 0 }).then(
        result => {
          this.changeSummary = result.changeSummary;
        }
      );
    }
  },
  methods: {
    ...mapWaitingActions("account", {
      getChangeSummary: "loading change summary",
      changeSubscription: "downgrading subscription",
      closeUserAccount: "closing account",
    }),
    ...mapActions("session", ["clearSession"]),
    handleSave: function() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        if (this.selectedOption === "Downgrade") {
          const payload = {
            subscriptionType: this.subscriptionType,
            billingCycle: this.billingPeriod,
          };
          this.changeSubscription(payload)
            .then(() => {
              this.$emit("close");
              this.$router.push({ name: "confirmation" }).catch(() => {});
            })
            .catch(() => {
              this.$emit("close");
            });
        }
        if (this.selectedOption === "Close Account") {
          this.closeUserAccount()
            .then(() => {
              this.clearSession();
              window.location.href = "/"
            })
            .catch(() => {
              this.$emit("close");
            });
        }
      }
    },
  },
  computed: {
    ...mapGetters("account", ["subscription"]),
    buttonText: function() {
      if (this.selectedOption !== "Downgrade") {
        return "Close Account";
      }
      return "Save";
    },
    transactionType: function() {
      if (this.changeSummary) {
        return getBillingTransactionTypeDescription(
          this.changeSummary.transactionType
        );
      }
    },
    transaction: function() {
      if (this.changeSummary) {
        if (this.changeSummary.transactionAmount !== 0) {
          return this.changeSummary.transactionAmountFormatted;
        }
      }
    },
  },
  validations: {
    agreement: {
      sameAs: sameAs(() => true),
    },
  },
};
</script>

<style></style>
